import { useState, type FormEvent } from 'react';
import { api, downloadBlob } from '../lib/api';
import { dayRangeError, istanbulDayRange, istanbulToday } from '../lib/istanbul-date';
import { PageHeader } from '../components/PageHeader';

type ReportKind = 'ncr-analysis' | 'kpi';
type Format = 'pdf' | 'json';

const REPORT_META: Record<
  ReportKind,
  { title: string; clause: string; description: string; procedure: string }
> = {
  'ncr-analysis': {
    title: 'Uygunsuzluk (NCR) Analizi',
    clause: '§8.7 · §9.1.3 · §10.2',
    description:
      'MES NCR verilerinden Pareto, trend ve karar (disposition) dağılımı üretir. SE-PR-08 ile uyumlu.',
    procedure: 'SE-PR-08',
  },
  kpi: {
    title: 'KPI ve Performans Raporu',
    clause: '§6.2 · §9.1.1 · §9.1.3',
    description:
      'SE-PR-26 formüllerine göre imalat uygun ürün %, hurda oranı, sevkiyat bazlı OTD (kademeli) ve Müşteri Memnuniyeti (40/40/20) hesaplar.',
    procedure: 'SE-PR-26 · SE-PR-24',
  },
};

export function ReportsPage() {
  const [kind, setKind] = useState<ReportKind>('ncr-analysis');
  const [from, setFrom] = useState(istanbulToday(-90));
  const [to, setTo] = useState(istanbulToday(0));
  const [busy, setBusy] = useState<Format | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<unknown>(null);

  const meta = REPORT_META[kind];
  const rangeError = dayRangeError(from, to);

  async function generate(fmt: Format, e?: FormEvent) {
    e?.preventDefault();
    // JSON Önizle submit düğmesi değil; tarayıcının min/max denetimi onu durdurmaz.
    if (rangeError) return;
    setBusy(fmt);
    setError(null);
    setPreview(null);
    try {
      const body = istanbulDayRange(from, to);
      if (fmt === 'json') {
        const json = await api.post<unknown>(`/v1/reports/${kind}?format=json`, body);
        setPreview(json);
      } else {
        const blob = await api.post<Blob>(`/v1/reports/${kind}?format=${fmt}`, body, { raw: true });
        const name = `${kind}-${from}-${to}.pdf`;
        downloadBlob(blob, name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rapor üretimi başarısız');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Rapor Üretici"
        subtitle="MES verisinden AS9100D uyumlu rapor — PDF / JSON çıktı"
      />

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <aside className="card p-3">
          <div className="mb-2 px-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Rapor türü
          </div>
          <div className="space-y-1">
            {(Object.keys(REPORT_META) as ReportKind[]).map((k) => {
              const isActive = k === kind;
              return (
                <button
                  type="button"
                  key={k}
                  onClick={() => setKind(k)}
                  className={`block w-full rounded-md px-3 py-2 text-left text-sm transition ${
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                      : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`}
                >
                  <div className="font-medium">{REPORT_META[k].title}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {REPORT_META[k].procedure}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="card p-5">
          <div className="mb-4">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {meta.title}
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              AS9100D: {meta.clause} · Prosedür: {meta.procedure}
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{meta.description}</p>
          </div>

          <form onSubmit={(e) => generate('pdf', e)} className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="from" className="label">
                Başlangıç
              </label>
              <input
                id="from"
                type="date"
                className="input"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="to" className="label">
                Bitiş
              </label>
              <input
                id="to"
                type="date"
                className="input"
                value={to}
                min={from}
                onChange={(e) => setTo(e.target.value)}
                required
              />
            </div>
            {rangeError && (
              <p className="text-xs text-rose-600 dark:text-rose-400 sm:col-span-2">{rangeError}</p>
            )}

            <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
              <button type="submit" className="btn-primary" disabled={busy !== null}>
                {busy === 'pdf' ? 'PDF üretiliyor…' : 'PDF İndir'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy !== null}
                onClick={() => generate('json')}
              >
                {busy === 'json' ? 'JSON…' : 'JSON Önizle'}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
              {error}
            </div>
          )}

          {preview !== null && (
            <details className="mt-4" open>
              <summary className="cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300">
                JSON önizleme
              </summary>
              <pre className="mt-2 max-h-96 overflow-auto rounded bg-slate-900 p-3 font-mono text-[11px] text-slate-100 dark:bg-slate-950 dark:ring-1 dark:ring-slate-800">
                {JSON.stringify(preview, null, 2)}
              </pre>
            </details>
          )}

          <div className="mt-6 rounded-md bg-slate-50 p-3 text-xs text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800/50 dark:text-slate-400 dark:ring-slate-700">
            <div className="font-medium text-slate-700 dark:text-slate-300">Not</div>
            FAIR (AS9102) raporu parça bazlı taslak gerektirir; bunu Faz 2'de bu sayfaya
            entegre edeceğiz. Şu an FAIR draft'ı için API: <code>POST /v1/reports/fair</code>.
          </div>
        </div>
      </div>
    </div>
  );
}
